import { CalendarClock } from 'lucide-react'
import type { CategoryState, ExtractedDate } from '@/types/extraction'
import { OverviewStatTile } from '@/components/overview/OverviewStatTile'
import { isRanAndEmpty } from '@/stores/extraction-store'

function formatShort(dateIso: string): string {
  try {
    return new Date(dateIso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  } catch {
    return ''
  }
}

function nextUpcoming(dates: ExtractedDate[]): ExtractedDate | null {
  const now = Date.now()
  return (
    dates
      .filter((d) => d.dateIso && new Date(d.dateIso).getTime() >= now)
      .sort((a, b) => new Date(a.dateIso!).getTime() - new Date(b.dateIso!).getTime())[0] ?? null
  )
}

export function KeyDatesTimeline({
  bidId,
  dates,
  categoryState,
  loading,
}: {
  bidId: string
  dates: ExtractedDate[]
  categoryState: CategoryState
  loading?: boolean
}) {
  const total = dates.length
  const next = nextUpcoming(dates)
  // Use the shared selector rather than re-deriving "ran and found nothing"
  // inline, so this copy can never disagree with the tab-collapse decision in
  // AnalysisSection or the empty state in ExtractionTable.
  const ranAndEmpty = isRanAndEmpty(categoryState)
  const sub = next?.dateIso
    ? `Next: ${formatShort(next.dateIso)}`
    : total > 0
      ? 'Milestones tracked'
      : ranAndEmpty
        ? 'Nothing found'
        : 'Not analysed yet'

  return (
    <OverviewStatTile
      icon={CalendarClock}
      label="Key dates"
      value={total}
      // `loading` only gates the tile's value, so the sub line has to be held
      // back here too — mid-run the counts are still zero and the tile would
      // otherwise claim a verdict while the analysis is still finding out.
      sub={loading ? undefined : sub}
      to={`/app/bids/${bidId}/qualify?view=key_dates`}
      loading={loading}
    />
  )
}
